import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { CONTACT } from "../../data/content";
import { scrollToId } from "../../lib/scroll";

const ParticleCanvas = () => {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let raf;
        let w = 0;
        let h = 0;
        const DPR = Math.min(window.devicePixelRatio || 1, 2);

        const resize = () => {
            w = canvas.offsetWidth;
            h = canvas.offsetHeight;
            canvas.width = w * DPR;
            canvas.height = h * DPR;
            ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
        };
        resize();

        const N = window.innerWidth < 640 ? 34 : 62;
        const nodes = Array.from({ length: N }, () => ({
            x: Math.random() * w,
            y: Math.random() * h,
            vx: (Math.random() - 0.5) * 0.32,
            vy: (Math.random() - 0.5) * 0.32,
            r: Math.random() * 1.5 + 0.6,
            gold: Math.random() < 0.14,
        }));

        const step = () => {
            ctx.clearRect(0, 0, w, h);
            for (const n of nodes) {
                n.x += n.vx;
                n.y += n.vy;
                if (n.x < 0 || n.x > w) n.vx *= -1;
                if (n.y < 0 || n.y > h) n.vy *= -1;
            }
            for (let i = 0; i < nodes.length; i++) {
                for (let j = i + 1; j < nodes.length; j++) {
                    const a = nodes[i];
                    const b = nodes[j];
                    const d = Math.hypot(a.x - b.x, a.y - b.y);
                    if (d < 130) {
                        ctx.strokeStyle = `rgba(0,229,255,${(1 - d / 130) * 0.13})`;
                        ctx.lineWidth = 1;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }
            for (const n of nodes) {
                ctx.fillStyle = n.gold ? "rgba(245,197,24,0.85)" : "rgba(0,229,255,0.65)";
                ctx.beginPath();
                ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
                ctx.fill();
            }
            raf = requestAnimationFrame(step);
        };
        step();
        window.addEventListener("resize", resize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={ref} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
};

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export const Hero = () => {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const contentY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const logoY = useTransform(scrollYProgress, [0, 1], [0, -80]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section
            id="home"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative min-h-screen flex items-center overflow-hidden noise-overlay"
        >
            <div className="absolute inset-0 hero-grid-bg" aria-hidden="true" />
            <div
                className="absolute -top-40 -left-40 h-[34rem] w-[34rem] rounded-full bg-[#00E5FF]/10 blur-[130px]"
                aria-hidden="true"
            />
            <div
                className="absolute -bottom-48 -right-32 h-[30rem] w-[30rem] rounded-full bg-[#F5C518]/10 blur-[130px]"
                aria-hidden="true"
            />
            <ParticleCanvas />

            <motion.img
                src="/assets/logo-mark.png"
                alt=""
                aria-hidden="true"
                style={{ y: logoY }}
                className="animate-float-slow pointer-events-none select-none absolute -right-16 top-1/2 -translate-y-1/2 w-[30rem] lg:w-[38rem] opacity-[0.13] hidden md:block"
            />

            <motion.div
                style={{ y: contentY, opacity: fade }}
                className="relative z-10 px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto w-full pt-32 pb-24"
            >
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="font-jbmono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-7 flex items-center gap-3"
                    data-testid="hero-eyebrow"
                >
                    <span className="inline-block h-px w-10 bg-[#00E5FF]/60" />
                    Animexa Tech Solutions — Dehradun, India
                </motion.p>

                <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl text-white max-w-4xl">
                    <MaskedLine delay={0.3}>Turning Ideas</MaskedLine>
                    <MaskedLine delay={0.42}>into</MaskedLine>
                    <MaskedLine delay={0.54}>
                        <span className="text-gradient-gold">Digital Reality</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.85 }}
                    className="mt-7 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed"
                    data-testid="hero-subline"
                >
                    Websites, Apps, Automation &amp; Digital Solutions for Businesses Ready to Grow.
                    <span className="block mt-3 text-sm sm:text-base text-slate-400">
                        From offline business to a professional digital presence — we design, build and
                        automate the technology your business needs.
                    </span>
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.05 }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-consultation"
                        onClick={() => scrollToId("#contact")}
                        className="group inline-flex items-center gap-2 rounded-full bg-[#F5C518] px-7 py-3.5 text-sm sm:text-base font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] hover:shadow-[0_0_36px_-6px_rgba(245,197,24,0.55)] active:scale-95"
                    >
                        Get Free Consultation
                        <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="hero-cta-services"
                        onClick={() => scrollToId("#services")}
                        className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 px-7 py-3.5 text-sm sm:text-base font-medium text-[#00E5FF] transition-all duration-300 hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/70 active:scale-95"
                    >
                        Explore Our Services
                    </button>
                    <a
                        data-testid="hero-cta-whatsapp"
                        href={CONTACT.waDefault}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 px-6 py-3.5 text-sm sm:text-base font-medium text-emerald-300 transition-all duration-300 hover:bg-emerald-400/10 active:scale-95"
                    >
                        <MessageCircle size={17} />
                        WhatsApp Us
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5, duration: 1 }}
                    className="mt-16 flex items-center gap-3 text-slate-500 font-jbmono text-[10px] uppercase tracking-[0.3em]"
                >
                    <ChevronDown size={14} className="animate-bounce text-[#F5C518]" />
                    Scroll to explore
                </motion.div>
            </motion.div>
        </section>
    );
};
